import { ColorPicker } from '../ui/ColorPicker';
import { PanelFrame } from './floating';
import { useBoard, useYMap } from './context';

export type GridStyle = 'dots' | 'lines' | 'none';

/** Board-level look, stored in the document's `meta` map and synced with the board. */
export interface BoardMetaSettings {
  bg?: string | null;
  grid?: GridStyle;
  gridSize?: number;
  /** One of LOOKS ids; undefined = the app theme. */
  look?: string;
  accent?: string | null;
  customCss?: string;
  /** Downscale imported images whose longest side exceeds this many pixels; unset/0 keeps dimensions. */
  importMaxSide?: number | null;
}

const IMPORT_SIDES: [number, string][] = [
  [0, 'Keep'],
  [2048, '2048'],
  [4096, '4096'],
  [8192, '8192'],
];

/** Built-in retro looks (palettes applied to the whole pane). */
export const LOOKS = [
  { id: 'paper', name: 'Paper' },
  { id: 'ice', name: 'Ice' },
  { id: 'amber', name: 'Amber' },
  { id: 'phosphor', name: 'Phosphor' },
  { id: 'blueprint', name: 'Blueprint' },
] as const;

/** Strips the few CSS features that could reach outside the board or load remote content. */
export function sanitizeCss(css: string): string {
  return css
    .replace(/<\/?style[^>]*>/gi, '')
    .replace(/@import[^;]*;?/gi, '')
    .replace(/@charset[^;]*;?/gi, '')
    .replace(/expression\s*\(/gi, '(')
    .replace(/behavior\s*:/gi, '')
    .replace(/url\s*\(\s*['"]?\s*(javascript|data|http|https|\/\/)[^)]*\)/gi, 'none')
    .slice(0, 20000);
}

export const GRID_SIZES = [16, 24, 32, 48, 64, 96, 128];

export function BoardSettings({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const meta = useYMap<unknown>(board.meta) as BoardMetaSettings;
  const set = (patch: BoardMetaSettings) => board.setMeta(patch as Record<string, unknown>);
  const grid = meta.grid ?? 'dots';
  return (
    <PanelFrame kind="settings" title="Board settings" className="board-settings" onClose={onClose}>
      <div className="insp-body">
        <div className="insp-label">Look</div>
        <div className="btn-row">
          <button type="button" className={`btn${!meta.look ? ' is-active' : ''}`} onClick={() => set({ look: null as unknown as string })}>
            Theme
          </button>
          {LOOKS.map((l) => (
            <button key={l.id} type="button" className={`btn${meta.look === l.id ? ' is-active' : ''}`} onClick={() => set({ look: l.id })}>
              {l.name}
            </button>
          ))}
        </div>
        <div className="insp-label">Accent ({meta.accent ?? 'default'})</div>
        <ColorPicker value={meta.accent ?? 'none'} onChange={(c) => set({ accent: c === 'none' ? null : c })} allowNone />
        <div className="insp-label">Background ({meta.bg ? meta.bg : 'theme'})</div>
        <ColorPicker value={meta.bg ?? 'none'} onChange={(c) => set({ bg: c === 'none' ? null : c })} allowNone />
        <div className="insp-label">Grid</div>
        <div className="btn-row">
          {(['dots', 'lines', 'none'] as GridStyle[]).map((g) => (
            <button key={g} type="button" className={`btn${grid === g ? ' is-active' : ''}`} onClick={() => set({ grid: g })}>
              {g}
            </button>
          ))}
        </div>
        {grid !== 'none' && (
          <>
            <div className="insp-label">Grid spacing</div>
            <div className="btn-row">
              {GRID_SIZES.map((n) => (
                <button key={n} type="button" className={`btn${(meta.gridSize ?? 32) === n ? ' is-active' : ''}`} onClick={() => set({ gridSize: n })}>
                  {n}
                </button>
              ))}
            </div>
            <div className="insp-muted">Grid snapping (menu) uses this spacing.</div>
          </>
        )}
        <div className="insp-label">Imported images: longest side (px)</div>
        <div className="btn-row">
          {IMPORT_SIDES.map(([n, label]) => (
            <button
              key={n}
              type="button"
              className={`btn${(meta.importMaxSide ?? 0) === n ? ' is-active' : ''}`}
              onClick={() => set({ importMaxSide: n === 0 ? null : n })}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="insp-muted">
          Keep stores images at their full size; a cap downscales larger ones as they are imported to this board. Format and quality are a device
          setting (menu → Storage &amp; imports).
        </div>
        <div className="insp-label">Custom CSS (scoped to this board)</div>
        <textarea
          className="css-input"
          rows={6}
          spellCheck={false}
          defaultValue={meta.customCss ?? ''}
          placeholder={'.item-note { box-shadow: none; }'}
          onBlur={(e) => set({ customCss: e.target.value.trim() || (null as unknown as string) })}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <div className="insp-muted">Applied after you leave the field. Imports and remote URLs are stripped.</div>
      </div>
    </PanelFrame>
  );
}
